import React, { RefObject } from 'react';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { DropdownObjectType, TreeNode } from '../../types/interfaces';
import { formatDateStringFromRaw } from '../../utils/date-utils';

export type TaskCardHeaderProps = {
  toastRef: RefObject<Toast | null>;
  isDetailIdValid: boolean;
  taskId: number | string | undefined;
  taskType: DropdownObjectType | TreeNode;
  name: string | undefined;
  asset: TreeNode;
  creationDate: string | undefined;
  isUpdate: boolean;
  isDirty: boolean;
  activeIndex: number;
  onSubmitSchemaLoad: () => Promise<void>;
  onDelete: () => void;
  onOpenMap: () => void;
  onCancel: () => void;
  onExportComments: () => void;
  onExportHistory: () => void;
};

function getLabel(item: string | TreeNode): string | null {
  if (typeof item !== 'string' && item.label) {
    return item.label;
  }
  return '';
}

export const TaskCardHeader: React.FC<TaskCardHeaderProps> = ({
  toastRef,
  isDetailIdValid,
  taskId,
  taskType,
  name,
  asset,
  creationDate,
  isUpdate,
  isDirty,
  activeIndex,
  onSubmitSchemaLoad,
  onDelete,
  onOpenMap,
  onCancel,
  onExportComments,
  onExportHistory
}) => {
  const copyToClipboard = async (
    text: string,
    message: string = 'Текст успешно скопирован в буфер обмена'
  ) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!success) {
          throw new Error('Copy command failed');
        }
      }

      toastRef.current?.show({
        severity: 'success',
        summary: 'Скопировано',
        detail: message,
        life: 2000
      });

      return true;
    } catch (error) {
      console.error('Failed to copy text: ', error);
      return false;
    }
  };

  const cardHeaderText = isDetailIdValid
    ? `Наряд №${taskId} ${taskType.label ?? `"${taskType.label}"`}${
        name ? ` "${name}"` : ''
      }${getLabel(asset) ? ` на объекте ${getLabel(asset)}` : ''}${
        creationDate ? ` от ${formatDateStringFromRaw(creationDate)}` : ''
      }`
    : 'Регистрация наряда';

  const menuButtons = (
    <div>
      <div className="flex justify-content-between">
        <div className="flex gap-2 task-card-header__buttons">
          <Button
            tooltip={isUpdate ? 'Сохранить' : 'Зарегистрировать наряд'}
            tooltipOptions={{ position: 'bottom' }}
            label={!isUpdate ? 'Регистрация' : undefined}
            icon="pi pi-check"
            rounded
            disabled={!isDirty}
            className={!isDirty ? 'p-button-secondary' : ''}
            onClick={async () => {
              try {
                await onSubmitSchemaLoad();
              } catch (error) {
                console.error('Error submitting schema load:', error);
              }
            }}
          />
          {isUpdate && (
            <Button
              tooltip="Удалить"
              tooltipOptions={{ position: 'bottom' }}
              icon="pi pi-trash"
              rounded
              onClick={onDelete}
              className="p-button-danger"
            />
          )}
          {!!isDetailIdValid && (
            <Button
              tooltip="Просмотреть наряд на карте"
              tooltipOptions={{ position: 'bottom' }}
              icon="pi pi-map"
              rounded
              className="p-button-secondary"
              onClick={onOpenMap}
            />
          )}
          <Button
            tooltip="Закрыть"
            tooltipOptions={{ position: 'bottom' }}
            icon="pi pi-times"
            rounded
            onClick={onCancel}
            className="p-button-secondary"
          />
          {isUpdate && activeIndex === 2 && (
            <Button
              key="export-tasks"
              type="button"
              icon="pi pi-download"
              label="Экспорт ленты примечаний"
              className="p-button-secondary tasks-nav-btn__roles"
              onClick={onExportComments}
            />
          )}
          {isUpdate && activeIndex === 4 && (
            <Button
              key="export-tasks"
              type="button"
              icon="pi pi-download"
              label="Экспорт истории"
              className="p-button-secondary tasks-export-btn__roles task-card-header__export-btn"
              onClick={onExportHistory}
            />
          )}
        </div>
      </div>
    </div>
  );

  const cardTitleContent = (
    <div className="flex justify-content-between task-card-header">
      {isDetailIdValid ? (
        <div className="flex flex-column font-normal">
          <div>{`Наряд: №${taskId}`}</div>
          <div className="task-card-header__subtitle">
            {taskType.label && (
              <>
                <strong>{taskType.label}</strong>
                {getLabel(asset) && (
                  <>
                    {' на объекте '}
                    <strong>{getLabel(asset)}</strong>
                  </>
                )}
                {' от '}
                <strong>{formatDateStringFromRaw(creationDate ?? '')}</strong>
                <i
                  className="pi pi-copy ml-2 copy-icon cursor-pointer task-card-header__copy-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(cardHeaderText);
                  }}
                />
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-column font-normal">Регистрация наряда</div>
      )}
      {menuButtons}
    </div>
  );

  return cardTitleContent;
};
